import { notification, message } from 'antd';
import type { ArgsProps } from 'antd/lib/notification';
import store from 'store';

import overwriteConfig from './components/notification-overwrite';
// import lt from './utils/long-timeout';
// import type { PushEvent } from './events/types';

export type NotificationType =
  | 'open'
  | 'success'
  | 'info'
  | 'warning'
  | 'error';

export interface NotificationRecord {
  id: string;
  type: NotificationType;
  title: string;
  description?: string;
  link?: string;
  timestamp: number;
  readed: boolean;
}

export interface NotifyConfig extends ArgsProps {
  message: string;
  description?: string;
  link?: string;
}

export interface NC {
  notify: (
    type: NotificationType,
    config: NotifyConfig,
    noPopup?: boolean,
  ) => void;
}

const NOTIFICATION_RECORDS_KEY = 'notification_records';
const NOTIFICATION_RECORDS_LIMIT = 99;

class NotificationCenterController implements NC {
  notification: typeof notification;

  message: typeof message;

  readonly events: {
    [key: string]: ((records: NotificationRecord[]) => void)[];
  } = {};

  static instance: NotificationCenterController;

  static getInstance() {
    if (!this.instance) {
      this.instance = new NotificationCenterController();
    }
    return this.instance;
  }

  constructor() {
    this.message = { ...message };
    this.notification = {
      ...notification,
    };

    this.notification.config({
      placement: 'top',
      top: 0,
      maxCount: 1,
    });
  }

  on(event: 'update', callback: (records: NotificationRecord[]) => void) {
    if (!Array.isArray(this.events[event])) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
    return this;
  }

  /**
   * 移除监听事件
   * @param event 事件名称
   * @param callback 如果提供callback则只移除对应事件
   */
  off(event: 'update', callback?: (records: NotificationRecord[]) => void) {
    if (!Array.isArray(this.events[event])) {
      return this;
    }
    if (typeof callback === 'undefined') {
      this.events[event] = [];
      return this;
    }
    const events = this.events[event];
    this.events[event] = events.filter((cb) => cb !== callback);

    return this;
  }

  replaceAntNotification(_notification: typeof notification) {
    console.log('replaced antd notification component');
    const proxy = (type: NotificationType) => {
      return (args: ArgsProps) => {
        if (typeof args.message !== 'string') {
          console.error(
            'Notification Faild: message must be string.',
            type,
            args,
          );
          this.notification[type](args);
          return;
        }

        if (
          typeof args.description !== 'undefined' &&
          typeof args.description !== 'string'
        ) {
          console.error(
            'Notification Faild: description must be undefined or string',
            type,
            args,
          );
          this.notification[type](args);
          return;
        }
        this.notify(type, args as NotifyConfig);
      };
    };

    _notification.open = this.notification.open;
    _notification.success = proxy('success');
    _notification.info = proxy('info');
    _notification.warning = proxy('warning');
    _notification.error = proxy('error');
    _notification.destroy = this.notification.destroy;

    return this;
  }

  replaceAntMessage(_message: typeof message) {
    console.log('replaced antd message component');
    const proxy = (type: NotificationType) => {
      return (content: string) => {
        if (typeof content !== 'string') {
          console.error(
            'Notification Failed: antd Message content must be string.',
            type,
            content,
          );
          this.notification[type]({ message: content });
          return;
        }
        this.notify(type, {
          message: content,
        });
      };
    };
    _message.success = proxy('success') as any;
    _message.error = proxy('error') as any;
    _message.info = proxy('info') as any;
    _message.warning = proxy('warning') as any;
    _message.loading = this.message.loading;
    return this;
  }

  //   registerNotify(event: PushEvent, cycle = 3600000) {
  //     const lastCheck = event.lastCheckTime;
  //     const now = Date.now();
  //     const lostTime = event.lastCheckTime;

  //     const task = () => {
  //       event.job(this, cycle);
  //       this.registerNotify(event, cycle);
  //     };

  //     if (lostTime >= cycle) {
  //       task();
  //     } else {
  //       lt.setTimeout(task, cycle - lostTime);
  //     }

  //     return this;
  //   }

  notify(type: NotificationType, config: NotifyConfig, noPopup = false) {
    if (noPopup === false) {
      this.notification[type](overwriteConfig(type, config));
    }
    this.writeRecord({
      id: String(new Date().valueOf()),
      type,
      title: config.message,
      description: config.description,
      link: config.link,
      timestamp: Date.now(),
      readed: false,
    });
  }

  close(key: string) {
    this.notification.destroy(key);
  }

  destroy() {
    this.notification.destroy();
  }

  writeRecord(record: NotificationRecord) {
    const records = this.readRecords();

    records.unshift(record);

    if (records.length > NOTIFICATION_RECORDS_LIMIT) {
      records.length = NOTIFICATION_RECORDS_LIMIT;
    }
    this.writeRecords(records);
  }

  writeRecords(records: NotificationRecord[]) {
    const storage = this.readAllRecords();
    const id: string = store.getState()?.session?.user?.id || 'unknowuser';
    if (id === 'unknowuser') {
      return;
    }
    storage[id] = records;
    try {
      localStorage.setItem(NOTIFICATION_RECORDS_KEY, JSON.stringify(storage));
    } catch (error) {
      console.error('write localStorage error', error);
    }

    const updateEvents = this.events.update;

    if (Array.isArray(updateEvents)) {
      updateEvents.forEach((cb) => {
        cb(records);
      });
    }
  }

  readAllRecords() {
    const storageTemp = localStorage.getItem(NOTIFICATION_RECORDS_KEY);
    if (typeof storageTemp !== 'string') {
      return {};
    }
    const storage = JSON.parse(storageTemp);
    return storage;
  }

  readRecords() {
    const storage = this.readAllRecords();
    const id: string = store.getState()?.session?.user?.id || 'unknowuser';
    const userStorage = storage[id];
    if (typeof userStorage !== 'undefined') {
      return userStorage as NotificationRecord[];
    }
    return [];
  }

  removeRecord(id?: string) {
    const records = this.readAllRecords();
    if (typeof id === 'string') {
      const newRecords = records.filter(
        (item: NotificationRecord) => item.id !== id,
      );
      this.writeRecords(newRecords);
      return;
    }

    this.writeRecords([]);
  }
}

(window as any).nc = NotificationCenterController.getInstance();

export default NotificationCenterController.getInstance();
